<template>
  <div class="overflow-x-auto w-full border rounded-lg">
    <div class="w-max min-w-full">
      <div
        class="hidden md:grid gap-2 px-4 py-2 font-semibold text-sm text-black"
        :style="{
          gridTemplateColumns: gridTemplateColumns,
          background: 'var(--Neutral-LightGray-5, #FAFAFA)'
        }"
      >
        <div
          v-for="(column, idx) in columns"
          :key="'head-' + idx"
          class="whitespace-nowrap px-2"
          :class="headerAlignClass(column)"
        >
          {{ column.label }}
        </div>
        <div class="px-2">{{ actionsLabel }}</div>
      </div>

      <div
        v-for="(row, index) in rows"
        :key="index"
        class="grid gap-2 items-center py-4 px-4 text-center text-sm"
        :style="{ gridTemplateColumns: gridTemplateColumns }"
      >
        <div
          v-for="(column, idx) in columns"
          :key="idx"
          class="flex flex-row items-center whitespace-nowrap"
          :class="cellAlignClass(column)"
        >
          <span class="font-bold md:hidden mr-2">{{ column.label }}:</span>

          <slot :name="`column-${column.key}`" :row="row" :value="getValue(row, column.key)">
            <p class="truncate flex items-center gap-2">
              <StatusDot v-if="column.key === 'name'" :status="row.status" />
              {{ getValue(row, column.key) }}
            </p>
          </slot>
        </div>

        <div class="flex flex-row justify-center items-center gap-2 mt-4 md:mt-0 whitespace-nowrap">
          <slot name="actions" :row="row" :index="index"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import StatusDot from './components/StatusDot.vue'

interface ColumnConfig {
  key: string
  label: string
  cols?: string
  width?: string
  align?: 'left' | 'center'
}

interface RowData {
  [key: string]: any
}

const { rows, columns, actionsLabel, actionsWidth } = defineProps<{
  rows: RowData[]
  columns: ColumnConfig[]
  actionsLabel?: string
  actionsWidth?: string
}>()

// `align` alinha cabeçalho E célula no mesmo eixo — sem ele o cabeçalho fica à
// esquerda enquanto a célula centraliza, e conteúdo estreito (ícone, badge) parece
// fora de eixo. Omitir a prop preserva o comportamento histórico.
const headerAlignClass = (column: ColumnConfig): string => {
  if (!column.align) return ''
  return column.align === 'center' ? 'text-center' : 'text-left'
}

const cellAlignClass = (column: ColumnConfig): string => {
  if (column.align) return column.align === 'center' ? 'justify-center' : 'justify-start'
  return column.key === 'name' ? 'justify-start' : 'justify-center'
}

const gridTemplateColumns = computed(() => {
  const colWidths = columns.map((c) => c.width || 'minmax(150px, 1fr)').join(' ')
  return `${colWidths} ${actionsWidth ?? 'auto'}`
})

const getValue = (obj: RowData, path: string): string => {
  const value = path.split('.').reduce((acc, part) => acc?.[part], obj)
  return value !== undefined && value !== null ? String(value) : '-'
}
</script>
